import isBoolean from "lodash/isBoolean";
import isNumber from "lodash/isNumber";
import srciLogicSdk from "@payair/srci-logic-sdk";
import { DcfSdkEnumsObj } from "@payair/srci-logic-sdk/models/DcfSdkEnum";
import { SrciCardTypeEnumsObj } from "../enums/SrciCardTypeEnum";
import { isConsumerNameFormatValid, isIso3166Alpha2, isIso4217, isLocaleValid, isString, } from "@payair/e-com-common-sdk/core/Validators";
import { isConsumerCountryCodeValid } from "../core/consumer/ConsumerInfoValidator";
import { CONSUMER_FIRST_NAME_PREFILL_LENGTH, CONSUMER_LAST_NAME_PREFILL_LENGTH, getConsumerNameLength, } from "../core/consumer/ConsumerValidationProfiles";
/** @internal */
export var validateSrciInitParams = function (srciInitParams) {
    var errors = [];
    if (!srciInitParams) {
        errors.push("The srciInitParams is not defined.");
    }
    else {
        if (!srciInitParams.currencyCode || !isIso4217(srciInitParams.currencyCode)) {
            errors.push("The currencyCode is not defined or in the wrong format.");
        }
        if (!srciInitParams.locale || !isLocaleValid(srciInitParams.locale)) {
            errors.push("The locale is not defined or in the wrong format.");
        }
        if (!srciInitParams.totalAmount || !isTransactionAmountValid(srciInitParams.totalAmount)) {
            errors.push("The totalAmount is not defined or in the wrong format.");
        }
        if (!srciInitParams.totalAmountLabel || !isString(srciInitParams.totalAmountLabel)) {
            errors.push("The totalAmountLabel is not defined or in the wrong format.");
        }
        if (!srciInitParams.transactionId || !isStringLengthValid(100, srciInitParams.transactionId)) {
            errors.push("The transactionId is not defined or in the wrong format.");
        }
        if (!srciInitParams.pspName || !isString(srciInitParams.pspName)) {
            errors.push("The pspName is not defined or in the wrong format.");
        }
        if (!srciInitParams.environment || (srciInitParams.environment !== "QA" && srciInitParams.environment !== "PROD")) {
            errors.push("The environment is not defined or in the wrong format. The supported values are 'QA' & 'PROD'");
        }
        if (srciInitParams.merchantId.size === 0) {
            errors.push("The merchantId must contain at least one srciDcfParam");
        }
        var allowedCardCountriesValidationResult = validateAllowedCardCountries(srciInitParams.allowedCardCountries);
        if (allowedCardCountriesValidationResult !== null) {
            errors.push(allowedCardCountriesValidationResult);
        }
        var allowedCardTypesValidationResult = validateAllowedCardTypes(srciInitParams.allowedCardTypes);
        if (allowedCardTypesValidationResult !== null) {
            errors.push(allowedCardTypesValidationResult);
        }
        for (var _i = 0, _a = Array.from(srciInitParams.merchantId); _i < _a.length; _i++) {
            var _b = _a[_i], sdk = _b[0], srciDcfParam = _b[1];
            if (!srciDcfParam.srciIdentificator.srcInitiatorId || !isString(srciDcfParam.srciIdentificator.srcInitiatorId)) {
                errors.push("The " + sdk + " srcInitiatorId is not defined or in the wrong format.");
            }
            if (!srciDcfParam.srciIdentificator.srciDpaId || !isString(srciDcfParam.srciIdentificator.srciDpaId)) {
                errors.push("The " + sdk + " srciDpaId is not defined or in the wrong format.");
            }
            validateDPATransactionOptions(srciDcfParam.dpaTransactionOptions, errors, sdk);
            var srciDpaData = srciDcfParam.srciDpaData;
            if ((srciDpaData === null || srciDpaData === void 0 ? void 0 : srciDpaData.dpaUri) && !isURLValid(srciDpaData.dpaUri)) {
                errors.push("The dpaUri for " + sdk + " is in the wrong format.");
            }
            if (sdk === DcfSdkEnumsObj.VISA) {
                if (srciDpaData &&
                    srciDpaData.dpaThreeDsPreference &&
                    srciDpaData.dpaThreeDsPreference !== "ONBEHALF" &&
                    srciDpaData.dpaThreeDsPreference !== "SELF" &&
                    srciDpaData.dpaThreeDsPreference !== "NONE" &&
                    srciDpaData.dpaThreeDsPreference !== "UNKNOWN") {
                    errors.push("The dpaThreeDsPreference for " + sdk + " is in the wrong format.");
                }
            }
        }
        if (srciInitParams.srciConsumer) {
            var consumer = srciInitParams.srciConsumer;
            var firstName = typeof consumer.firstName === "string" ? consumer.firstName.trim() : consumer.firstName;
            var lastName = typeof consumer.lastName === "string" ? consumer.lastName.trim() : consumer.lastName;
            if (consumer.emailAddress &&
                (!isEmailValid(consumer.emailAddress) || !isStringLengthValid(255, consumer.emailAddress))) {
                errors.push("The email is in the wrong format.");
            }
            if (firstName && !isString(firstName)) {
                errors.push("The firstName is in the wrong format.");
            }
            else if (firstName &&
                (getConsumerNameLength(firstName) < CONSUMER_FIRST_NAME_PREFILL_LENGTH.min ||
                    getConsumerNameLength(firstName) > CONSUMER_FIRST_NAME_PREFILL_LENGTH.max)) {
                errors.push("The firstName is in the wrong format. The string must be between ".concat(CONSUMER_FIRST_NAME_PREFILL_LENGTH.min, " and ").concat(CONSUMER_FIRST_NAME_PREFILL_LENGTH.max, " characters."));
            }
            else if (firstName && !isConsumerNameFormatValid(firstName)) {
                errors.push("The firstName is in the wrong format. Use only letters, spaces, apostrophes, or hyphens.");
            }
            if (lastName && !isString(lastName)) {
                errors.push("The lastName is in the wrong format.");
            }
            else if (lastName &&
                (getConsumerNameLength(lastName) < CONSUMER_LAST_NAME_PREFILL_LENGTH.min ||
                    getConsumerNameLength(lastName) > CONSUMER_LAST_NAME_PREFILL_LENGTH.max)) {
                errors.push("The lastName is in the wrong format. The string must be between ".concat(CONSUMER_LAST_NAME_PREFILL_LENGTH.min, " and ").concat(CONSUMER_LAST_NAME_PREFILL_LENGTH.max, " characters."));
            }
            else if (lastName && !isConsumerNameFormatValid(lastName)) {
                errors.push("The lastName is in the wrong format. Use only letters, spaces, apostrophes, or hyphens.");
            }
            if (consumer.nationalIdentifier && !isStringLengthValid(20, consumer.nationalIdentifier)) {
                errors.push("The nationalIdentifier is in the wrong format. The string cannot be longer then 20 characters.");
            }
            if (consumer.countryCode !== undefined && !isConsumerCountryCodeValid(consumer.countryCode)) {
                errors.push("The srciConsumer.countryCode is in the wrong format. Use ISO 3166-1 alpha-2.");
            }
            if (consumer.mobileNumber) {
                if (!consumer.mobileNumber.countryCode ||
                    !isString(consumer.mobileNumber.countryCode) ||
                    consumer.mobileNumber.countryCode.length > 4) {
                    errors.push("The mobileNumber.countryCode is not defined or in the wrong format.");
                }
                if (!consumer.mobileNumber.phoneNumber ||
                    !isString(consumer.mobileNumber.phoneNumber) ||
                    consumer.mobileNumber.phoneNumber.length < 4 ||
                    consumer.mobileNumber.phoneNumber.length > 14) {
                    errors.push("The mobileNumber.phoneNumber is not defined or in the wrong format.");
                }
            }
            if (consumer.consumerIdentity) {
                if (consumer.consumerIdentity.identityProvider && !isString(consumer.consumerIdentity.identityProvider)) {
                    errors.push("The identityProvider is not defined or in the wrong format.");
                }
                if (consumer.consumerIdentity.identityType && consumer.consumerIdentity.type) {
                    errors.push("The identityType & type cannot be defined at the same time.");
                }
                if (consumer.consumerIdentity.identityType &&
                    consumer.consumerIdentity.identityType !== "EMAIL_ADDRESS" &&
                    consumer.consumerIdentity.identityType !== "MOBILE_PHONE_NUMBER") {
                    errors.push("The identityType is not defined or in the wrong format. The supported values are 'EMAIL_ADDRESS' & 'MOBILE_PHONE_NUMBER'");
                }
                if (consumer.consumerIdentity.type &&
                    consumer.consumerIdentity.type !== "EMAIL" &&
                    consumer.consumerIdentity.type !== "MOBILE_NUMBER" &&
                    consumer.consumerIdentity.type !== "CUSTOM_IDENTIFIER" &&
                    consumer.consumerIdentity.type !== "FUTURE") {
                    errors.push("The type is not defined or in the wrong format. The supported values are 'EMAIL', 'MOBILE_NUMBER', 'CUSTOM_IDENTIFIER' & 'FUTURE' ");
                }
                if (consumer.consumerIdentity.identityValue && !isString(consumer.consumerIdentity.identityValue)) {
                    errors.push("The identityValue is not defined or in the wrong format.");
                }
            }
            if (srciInitParams.srciUiParams) {
                if (srciInitParams.srciUiParams.isCloseButtonEnabled &&
                    !isBoolean(srciInitParams.srciUiParams.isCloseButtonEnabled)) {
                    errors.push("The isCloseButtonEnabled is in the wrong format.");
                }
                if (srciInitParams.srciUiParams.isDcfFlowPopUp && !isBoolean(srciInitParams.srciUiParams.isDcfFlowPopUp)) {
                    errors.push("The isDcfFlowPopUp is in the wrong format.");
                }
            }
        }
        if (srciInitParams.srciBillingAddress) {
            var srciBillingAddress = srciInitParams.srciBillingAddress;
            if (srciBillingAddress.addressId &&
                srciBillingAddress.addressId.length > 0 &&
                !isUUIDValid(srciBillingAddress.addressId)) {
                errors.push("The addressId is in the wrong format.");
            }
            if (srciBillingAddress.name &&
                srciBillingAddress.name.length > 0 &&
                !isStringLengthValid(60, srciBillingAddress.name)) {
                errors.push("The name is in wrong format. The string cannot be longer then 60 characters.");
            }
            if (srciBillingAddress.line1 &&
                srciBillingAddress.line1.length > 0 &&
                !isStringLengthValid(75, srciBillingAddress.line1)) {
                errors.push("The line1 is in wrong format. The string cannot be longer then 75 characters.");
            }
            if (srciBillingAddress.line2 &&
                srciBillingAddress.line2.length > 0 &&
                !isStringLengthValid(75, srciBillingAddress.line2)) {
                errors.push("The line2 is in wrong format. The string cannot be longer then 75 characters.");
            }
            if (srciBillingAddress.line3 &&
                srciBillingAddress.line3.length > 0 &&
                !isStringLengthValid(75, srciBillingAddress.line3)) {
                errors.push("The line3 is in wrong format. The string cannot be longer then 75 characters.");
            }
            if (srciBillingAddress.city &&
                srciBillingAddress.city.length > 0 &&
                !isStringLengthValid(50, srciBillingAddress.city)) {
                errors.push("The city is in wrong format. The string cannot be longer then 50 characters.");
            }
            if (srciBillingAddress.state &&
                srciBillingAddress.state.length > 0 &&
                !isStringLengthValid(30, srciBillingAddress.state)) {
                errors.push("The state is in wrong format. The string cannot be longer then 30 characters.");
            }
            if (srciBillingAddress.countryCode &&
                srciBillingAddress.countryCode.length > 0 &&
                !isArrayIso3166Alpha2([srciBillingAddress.countryCode])) {
                errors.push("The srciBillingAddress.country code is in the wrong format.");
            }
            if (srciBillingAddress.zip && !isStringLengthValid(16, srciBillingAddress.zip)) {
                errors.push("The zip is in wrong format. The string cannot be longer then 16 characters.");
            }
        }
    }
    return errors;
};
var isArrayIso3166Alpha2 = function (array) {
    return array.filter(function (item) {
        if (!isIso3166Alpha2(item)) {
            return item;
        }
    }).length === 0;
};
var validateDPATransactionOptions = function (dpaTransactionOptions, errors, sdk) {
    if (dpaTransactionOptions) {
        var billingCountries = dpaTransactionOptions.dpaAcceptedBillingCountries;
        var shippingCountries = dpaTransactionOptions.dpaAcceptedShippingCountries;
        var billingPrefence = dpaTransactionOptions.dpaBillingPreference;
        var shippingPrefence = dpaTransactionOptions.dpaShippingPreference;
        var locale = dpaTransactionOptions.dpaLocale;
        //transactionType, payloadTypeIndicatorCheckout and payloadTypeIndicatorPayload
        //are intentionally left out, since they are not yet supported/defined by Mastercard.
        if (billingCountries && billingCountries.length > 0 && !isArrayIso3166Alpha2(billingCountries)) {
            errors.push("One or more of the values in dpaAcceptedBillingCountries for " + sdk + " are incorrect.");
        }
        if (shippingCountries && shippingCountries.length > 0 && !isArrayIso3166Alpha2(shippingCountries)) {
            errors.push("One or more of the values in dpaAcceptedShippingCountries for " + sdk + " are incorrect.");
        }
        if (billingPrefence &&
            billingPrefence !== "FULL" &&
            billingPrefence !== "NONE" &&
            billingPrefence !== "ALL" &&
            billingPrefence !== "POSTAL_COUNTRY") {
            errors.push("The value of dpaBillingPreference for " + sdk + " is incorrect.");
        }
        if (shippingPrefence &&
            shippingPrefence !== "FULL" &&
            shippingPrefence !== "NONE" &&
            shippingPrefence !== "ALL" &&
            shippingPrefence !== "POSTAL_COUNTRY") {
            errors.push("The value of dpaShippingPreference for " + sdk + " is incorrect.");
        }
        if (!locale || !isLocaleValid(locale)) {
            errors.push("The locale for " + sdk + " is not defined or in the wrong format.");
        }
        var transactionAmount = dpaTransactionOptions.transactionAmount;
        if (!transactionAmount) {
            errors.push("The transactionAmount for " + sdk + " is not defined.");
        }
        else {
            if (!transactionAmount.transactionAmount || !isNumber(transactionAmount.transactionAmount)) {
                errors.push("The transactionAmount in the transactionAmount object for " + sdk + " is not defined or in the wrong format.");
            }
            if (!isIso4217(transactionAmount.transactionCurrencyCode)) {
                errors.push("The transactionCurrencyCode for " + sdk + " is in the wrong format.");
            }
        }
    }
};
var validateAllowedCardCountries = function (allowedCardCountries) {
    if (allowedCardCountries === undefined) {
        return null;
    }
    if (!Array.isArray(allowedCardCountries)) {
        return "The allowedCardCountries must be an array";
    }
    if (allowedCardCountries.length <= 0) {
        return "The allowedCardCountries array must not be empty";
    }
    if (allowedCardCountries.includes(null) ||
        allowedCardCountries.includes(undefined) ||
        allowedCardCountries.includes(false)) {
        return "The allowedCardCountries cannot contain null, undefined or false";
    }
    var isAllowedCardCountriesIso = isArrayIso3166Alpha2(allowedCardCountries);
    if (!isAllowedCardCountriesIso) {
        return "allowedCardCountries values are in wrong format. Only two-letter ISO 3166-1 alpha 2 format is accepted";
    }
    return null;
};
var validateAllowedCardTypes = function (allowedCardTypes) {
    if (allowedCardTypes === undefined) {
        return null;
    }
    if (!Array.isArray(allowedCardTypes)) {
        return "The allowedCardTypes must be an array";
    }
    if (allowedCardTypes.length <= 0) {
        return "The allowedCardTypes array must not be empty";
    }
    if (allowedCardTypes.includes(null) || allowedCardTypes.includes(undefined) || allowedCardTypes.includes(false)) {
        return "The allowedCardTypes cannot contain null, undefined or false";
    }
    var cardTypesValues = Object.values(SrciCardTypeEnumsObj);
    var notMatchingCardTypes = allowedCardTypes.filter(function (allowedCardType) { return !cardTypesValues.includes(allowedCardType); });
    if (notMatchingCardTypes.length > 0) {
        return "Wrong allowedCardTypes values: ".concat(notMatchingCardTypes.join(", "), ". Use only: ").concat(cardTypesValues.join(", "));
    }
    return null;
};
var isUUIDValid = function (uuid) {
    if (!isString(uuid)) {
        return false;
    }
    var regex = new RegExp("^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$");
    return regex.test(uuid);
};
var isURLValid = function (url) {
    if (!isString(url)) {
        return false;
    }
    var regex = new RegExp("^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$", "i");
    return regex.test(url);
};
var isEmailValid = function (email) {
    if (!isString(email)) {
        return false;
    }
    return srciLogicSdk.isEmailValid(email);
};
var isStringLengthValid = function (maxLength, stringData) {
    if (!isString(stringData)) {
        return false;
    }
    var returnData;
    if (stringData.length <= maxLength) {
        returnData = true;
    }
    else {
        returnData = false;
    }
    return returnData;
};
var isTransactionAmountValid = function (transactionAmount) {
    if (!isString(transactionAmount)) {
        return false;
    }
    var regex = new RegExp("^[0-9]{1,9}((\\.|\\,)[0-9]{0,4})?$");
    return regex.test(transactionAmount);
};
